import React from 'react';
import { FC, useCallback, useState } from 'react';

import { Button, Label, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { ApiError, shareApi } from 'client/api';
import { useFetchingHandler } from 'client/hooks/useFetchingHandler';
import { InstallableApp, Skill } from 'client/model/skill';
import * as keyset from 'i18n/Access';

import { Field } from '../form/Field/Field';
import { TextInputWithCopy } from '../form/TextInputWithCopy/TextInputWithCopy';

import styles from './SharingPage.module.scss';

const t = i18n(keyset);

interface Props {
    aliceApp: InstallableApp | Skill;
    sharingAvailable?: boolean;
}

export const SharingPageGenerateLink: FC<Props> = ({ aliceApp, sharingAvailable }) => {
    const [oneTimeLink, setOneTimeLink] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const { add: addToast } = useToaster();

    const [createOneTimeShare, generationState] = useFetchingHandler({
        handler: shareApi.createOneTimeShare,
        throwErrors: true,
    });

    const generateOneTimeLink = useCallback(async () => {
        setErrorMessage('');

        try {
            const oneTimeShareKey = await createOneTimeShare({ skillId: aliceApp.id }, aliceApp.channel);

            const shareLink = `${__appConfig.app.shareBaseUrl}?key=${oneTimeShareKey}`;

            setOneTimeLink(shareLink);
        } catch (e) {
            console.error(e);

            const message = e instanceof ApiError ? e.message : t('error');

            setErrorMessage(message);
            addToast({ title: message, theme: 'danger', name: 'ONE_TIME_SHARE_ERROR' });
        }
    }, [aliceApp.id, aliceApp.channel, createOneTimeShare, addToast]);

    const isGenerating = generationState === 'loading';
    const isError = generationState === 'error';

    return (
        <Field
            fieldTitle={t('links-header')}
            fieldDescription={t('app-link-description')}
            error={isError ? errorMessage || t('error') : undefined}
        >
            <TextInputWithCopy value={oneTimeLink} disabled={isGenerating} />
            <Button onClick={generateOneTimeLink} loading={isGenerating} size="l" disabled={!sharingAvailable}>
                {t('links-button-generate')}
            </Button>
            {sharingAvailable === false ? (
                <Label className={styles.sharingWarning} theme="warning" size="s">
                    {t('max-number-reached')}
                </Label>
            ) : null}
        </Field>
    );
};
